import { Event, EmployeeService, PartnerService } from '@geovelo-frontends/commons';
import { Box, Toolbar } from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router';

import { AppContext } from '../../app/context';
import { getCurrentContract } from '../../hooks/employees';
import useHighlightedEventsQuery from '../../hooks/use-highlighted-events-query';
import usePartnerGeogroupQuery from '../../hooks/use-partner-geogroup-query';
import { sizeRanges } from '../../pages/admin/data/size-range-form';
import AppDialog from '../../pages/home/app-dialog';

import CompanySizeDialog from './company-size-dialog';
import CongratsDialog from './congrats-dialog';
import CreationReasonDialog from './creation-reason-dialog';
import Drawer, { drawerBreakpoint, drawerWidth } from './drawer';
import EventDialog from './event-dialog';
import FirstMembersAddedDialog from './first-members-added-dialog';
import Header from './header';
import JobDialog from './job-dialog';
import MobilitySurveyDialog from './mobility-survey-dialog';
import PremiumContractDialog from './premium-contract-period-dialog';
import StandardContractDialog from './standard-contract-period-dialog';
import WelcomeDialog from './welcome-dialog';

function Layout(): React.JSX.Element {
  const {
    app: { currentEvent },
    partner: {
      current: currentPartner,
      type: partnerType,
      getStartedProgression,
      firstMembersAddedDialogOpenRef,
      eventDialogOpen: _eventDialogOpen,
      mobilitySurveyDialogOpen: _mobilitySurveyDialogOpen,
    },
    user: { employee, home: userHome, creationReasonDialogSeen },
    actions: {
      setUserEmployee,
      setUserCreationReasonDialogSeen,
      openEventDialog: _openEventDialog,
      openMobilitySurveyDialog: _openMobilitySurveyDialog,
    },
  } = useContext(AppContext);
  const { data: geogroup } = usePartnerGeogroupQuery(currentPartner);
  const { data: highlightedEvents } = useHighlightedEventsQuery(currentPartner, geogroup);
  const contract = currentPartner && getCurrentContract(currentPartner);
  const [drawerOpenOnMobile, openDrawerOnMobile] = useState(false);
  const [standardContractDialogOpen, openStandardContractDialog] = useState<string | null>(null);
  const [premiumContractDialogOpen, openPremiumContractDialog] = useState<string | null>(null);
  const [companySizeDialogOpen, openCompanySizeDialog] = useState(false);
  const [jobDialogOpen, openJobDialog] = useState(false);
  const [firstMembersAddedDialogOpen, openFirstMembersAddedDialog] = useState(false);
  const [congratsDialogOpen, openCongratsDialog] = useState(false);
  const { state: locationState, pathname } = useLocation();
  const isOnEventOrChallengePage = /^\/(events|challenges)(\/|$)/.test(pathname);
  const [welcomeDialogOpen, openWelcomeDialog] = useState(false);
  const [eventDialogOpen, openEventDialog] = useState<string | null>(null);
  const [eventDialogEvent, setEventDialogEvent] = useState<Event | null>(null);
  const [mobilitySurveyDialogOpen, openMobilitySurveyDialog] = useState<string | null>(null);
  const [creationReasonDialogOpen, openCreationReasonDialog] = useState(false);
  const [appDialogOpen, openAppDialog] = useState(false);
  const adminDialogOpenRef = useRef(false);
  const userDialogOpenRef = useRef(false);

  useEffect(() => {
    if (
      highlightedEvents !== undefined &&
      currentPartner &&
      partnerType &&
      partnerType !== 'holding' &&
      contract &&
      employee?.isAdmin
    ) {
      const { startDateTime: _startDateTime } = contract;
      const startDateTime = _startDateTime?.format('YYYY-MM-DD') || '';
      const {
        surveyAnswers: {
          welcomeDialogSeen,
          congratsDialogSeen,
          [`standardContractDialogSeen_${startDateTime}`]: standardContractDialogSeen,
          [`premiumContractDialogSeen_${startDateTime}`]: premiumContractDialogSeen,
          [`mobilitySurveyDialogSeen_${startDateTime}`]: mobilitySurveyDialogSeen,
        },
      } = employee;

      const firstUnseenForEventDialog = highlightedEvents?.find(
        (e) => !employee.surveyAnswers[`eventDialogSeen_${e.id}`],
      );

      if (adminDialogOpenRef.current) return;

      if (
        !welcomeDialogSeen &&
        !welcomeDialogOpen &&
        !locationState?.challengeId &&
        !currentEvent?.id
      ) {
        adminDialogOpenRef.current = true;
        handleWelcomeDialogOpen();
      } else if (
        partnerType === 'standard' &&
        !standardContractDialogSeen &&
        !isOnEventOrChallengePage
      ) {
        adminDialogOpenRef.current = true;
        openStandardContractDialog(`standardContractDialogSeen_${startDateTime}`);
      } else if (
        partnerType === 'premium' &&
        !premiumContractDialogSeen &&
        !isOnEventOrChallengePage
      ) {
        adminDialogOpenRef.current = true;
        openPremiumContractDialog(`premiumContractDialogSeen_${startDateTime}`);
      } else if (!creationReasonDialogSeen && !creationReasonDialogOpen) {
        adminDialogOpenRef.current = true;
        if (currentEvent?.id) {
          setUserCreationReasonDialogSeen(true);
          if (currentPartner) {
            PartnerService.editPartner(currentPartner, {
              creationReasons: [`CHALLENGE_EVENT_${currentEvent?.id}`],
            }).catch(console.error);
          }
        } else {
          openCreationReasonDialog(true);
        }
      } else if (
        !sizeRanges.some(
          ({ min, max }) =>
            geogroup?.sizeRange?.min === min && geogroup?.sizeRange?.max === (max ?? 100_000_000),
        ) &&
        !companySizeDialogOpen
      ) {
        adminDialogOpenRef.current = true;
        openCompanySizeDialog(true);
      } else if (_eventDialogOpen && firstUnseenForEventDialog) {
        adminDialogOpenRef.current = true;
        setEventDialogEvent(firstUnseenForEventDialog);
        openEventDialog(`eventDialogSeen_${firstUnseenForEventDialog.id}`);
      } else if (_mobilitySurveyDialogOpen) {
        if (!mobilitySurveyDialogOpen && !mobilitySurveyDialogSeen) {
          adminDialogOpenRef.current = true;
          openMobilitySurveyDialog(`mobilitySurveyDialogSeen_${startDateTime}`);
        } else {
          _openMobilitySurveyDialog(false);
        }
      } else if (!employee.surveyAnswers?.job && !jobDialogOpen) {
        adminDialogOpenRef.current = true;
        openJobDialog(true);
      } else if (
        firstMembersAddedDialogOpenRef &&
        firstMembersAddedDialogOpenRef.current &&
        !firstMembersAddedDialogOpen
      ) {
        adminDialogOpenRef.current = true;
        openFirstMembersAddedDialog(true);
      } else if (getStartedProgression?.allDone && !congratsDialogSeen && !congratsDialogOpen) {
        adminDialogOpenRef.current = true;
        handleCongratsDialogOpen();
      }
    }
  }, [
    highlightedEvents,
    currentPartner,
    geogroup,
    employee,
    getStartedProgression,
    contract,
    welcomeDialogOpen,
    standardContractDialogOpen,
    premiumContractDialogOpen,
    companySizeDialogOpen,
    jobDialogOpen,
    firstMembersAddedDialogOpenRef?.current,
    _eventDialogOpen,
    _mobilitySurveyDialogOpen,
    congratsDialogOpen,
    creationReasonDialogOpen,
  ]);

  useEffect(() => {
    if (currentPartner && employee && !employee.isAdmin && userHome !== undefined) {
      const firstUnseenForEventDialog = highlightedEvents?.find(
        (e) => !employee.surveyAnswers[`eventDialogSeen_${e.id}`],
      );

      if (userDialogOpenRef.current) return;

      if (
        !employee.surveyAnswers.welcomeDialogSeen &&
        !welcomeDialogOpen &&
        !locationState?.challengeId &&
        !currentEvent?.id
      ) {
        userDialogOpenRef.current = true;
        handleWelcomeDialogOpen();
      } else if (!employee.surveyAnswers.downloadDialogSeen && !appDialogOpen) {
        userDialogOpenRef.current = true;
        handleAppDialogOpen();
      } else if (_eventDialogOpen && firstUnseenForEventDialog) {
        userDialogOpenRef.current = true;
        setEventDialogEvent(firstUnseenForEventDialog);
        openEventDialog(`eventDialogSeen_${firstUnseenForEventDialog.id}`);
      }
    }
  }, [currentPartner, employee, welcomeDialogOpen, appDialogOpen, _eventDialogOpen]);

  function handleDrawerToggle() {
    openDrawerOnMobile(!drawerOpenOnMobile);
  }

  async function handleWelcomeDialogOpen() {
    if (!currentPartner || !employee || employee.surveyAnswers.welcomeDialogSeen) return;

    openWelcomeDialog(true);

    try {
      const updatedEmployee = await EmployeeService.updateEmployee(currentPartner, employee.id, {
        surveyAnswers: { ...employee.surveyAnswers, welcomeDialogSeen: true },
      });

      setUserEmployee(updatedEmployee);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleCongratsDialogOpen() {
    if (!currentPartner || !employee || employee.surveyAnswers.congratsDialogSeen) return;

    openCongratsDialog(true);

    try {
      const updatedEmployee = await EmployeeService.updateEmployee(currentPartner, employee.id, {
        surveyAnswers: { ...employee.surveyAnswers, congratsDialogSeen: true },
      });

      setUserEmployee(updatedEmployee);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleAppDialogOpen() {
    if (!currentPartner || !employee || employee.surveyAnswers.downloadDialogSeen) return;

    openAppDialog(true);

    try {
      const updatedEmployee = await EmployeeService.updateEmployee(currentPartner, employee.id, {
        surveyAnswers: { ...employee.surveyAnswers, downloadDialogSeen: true },
      });

      setUserEmployee(updatedEmployee);
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <>
      <Box display="flex" flexGrow={1} sx={{ overflowY: 'auto' }}>
        <Header handleDrawerToggle={handleDrawerToggle} />
        <Drawer openOnMobile={drawerOpenOnMobile} toggle={handleDrawerToggle} />
        <Box
          component="main"
          flexGrow={1}
          sx={(theme) => ({
            overflowX: 'hidden',
            overflowY: 'auto',
            [theme.breakpoints.up(drawerBreakpoint)]: {
              width: `calc(100% - ${drawerWidth}px)`,
            },
          })}
        >
          <Toolbar
            sx={(theme) => ({
              display: 'block',
              [theme.breakpoints.up(drawerBreakpoint)]: { display: 'none' },
            })}
          />
          <Outlet />
        </Box>
      </Box>
      <StandardContractDialog
        onClose={() => {
          adminDialogOpenRef.current = false;
          openStandardContractDialog(null);
        }}
        open={Boolean(standardContractDialogOpen)}
        surveyAnswerKey={standardContractDialogOpen}
      />
      <PremiumContractDialog
        onClose={() => {
          adminDialogOpenRef.current = false;
          openPremiumContractDialog(null);
        }}
        open={Boolean(premiumContractDialogOpen)}
        surveyAnswerKey={premiumContractDialogOpen}
      />
      <CompanySizeDialog
        onClose={() => {
          adminDialogOpenRef.current = false;
          openCompanySizeDialog(false);
        }}
        open={companySizeDialogOpen}
      />
      <JobDialog
        onClose={() => {
          adminDialogOpenRef.current = false;
          openJobDialog(false);
        }}
        open={jobDialogOpen}
      />
      <FirstMembersAddedDialog
        onClose={() => {
          adminDialogOpenRef.current = false;
          if (firstMembersAddedDialogOpenRef) firstMembersAddedDialogOpenRef.current = false;
          openFirstMembersAddedDialog(false);
        }}
        open={firstMembersAddedDialogOpen}
      />
      <CongratsDialog
        onClose={() => {
          adminDialogOpenRef.current = false;
          openCongratsDialog(false);
        }}
        open={congratsDialogOpen}
      />
      <WelcomeDialog
        onClose={() => {
          userDialogOpenRef.current = false;
          adminDialogOpenRef.current = false;
          openWelcomeDialog(false);
        }}
        open={welcomeDialogOpen}
      />
      <CreationReasonDialog
        onClose={() => {
          adminDialogOpenRef.current = false;
          openCreationReasonDialog(false);
        }}
        open={creationReasonDialogOpen}
      />
      <AppDialog
        onClose={() => {
          userDialogOpenRef.current = false;
          openAppDialog(false);
        }}
        open={appDialogOpen}
      />
      <EventDialog
        event={eventDialogEvent}
        onClose={() => {
          adminDialogOpenRef.current = false;
          _openEventDialog(false);
          openEventDialog(null);
        }}
        open={Boolean(eventDialogOpen)}
        surveyAnswerKey={eventDialogOpen}
      />
      <MobilitySurveyDialog
        onClose={() => {
          adminDialogOpenRef.current = false;
          _openMobilitySurveyDialog(false);
          openMobilitySurveyDialog(null);
        }}
        open={Boolean(mobilitySurveyDialogOpen)}
        surveyAnswerKey={mobilitySurveyDialogOpen}
      />
    </>
  );
}

export default Layout;
